import { useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useUser,
  useUserGroupMemberships,
  UserProfileCard,
  LoadingSpinner,
  ApiErrorDisplay,
  UserUmrsGrantsSection,
} from '@authifi/clique-react-ui';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const numericUserId = Number.parseInt(userId ?? '', 10);
  const hasValidUserId = Number.isInteger(numericUserId) && numericUserId > 0;
  const { data: user, isLoading, isError, error } = useUser(hasValidUserId ? numericUserId : 0);
  const {
    data: groups,
    isLoading: groupsLoading,
    isError: groupsIsError,
    error: groupsError,
    refetch: refetchGroups,
  } = useUserGroupMemberships(hasValidUserId ? numericUserId : 0);

  const handleBack = useCallback(() => {
    navigate('/users');
  }, [navigate]);

  const handleSuccess = useCallback((message: string) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
  }, []);

  const handleError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  }, []);

  if (!hasValidUserId) {
    return (
      <ApiErrorDisplay
        error={new Error('Invalid user identifier')}
        resourceLabel="user"
        onRetry={handleBack}
        retryLabel="Back to Users"
      />
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading user..." />;
  }

  if (isError) {
    return (
      <ApiErrorDisplay
        error={error}
        resourceLabel="user"
        onRetry={handleBack}
        retryLabel="Back to Users"
      />
    );
  }

  if (!user) return null;

  return (
    <div>
      <Toast ref={toast} />
      <div className="flex items-center gap-3 mb-6">
        <Button
          type="button"
          icon="pi pi-arrow-left"
          onClick={handleBack}
          text
          rounded
          severity="secondary"
          aria-label="Back to users"
        />
        <h2 className="text-2xl font-semibold text-[var(--clique-fg)]">User Details</h2>
      </div>
      <UserProfileCard
        user={user}
        groups={groups}
        groupsLoading={groupsLoading}
        groupsError={groupsIsError ? groupsError : undefined}
        onGroupsRetry={() => void refetchGroups()}
      />
      <UserUmrsGrantsSection
        userId={numericUserId}
        onSuccess={handleSuccess}
        onError={handleError}
      />
    </div>
  );
}
